/**
 * Watchlist — the listings a buyer has starred (web).
 *
 * A CLIENT-ONLY feature (no backend watchlist endpoint yet — TD). The starred
 * set is the localStorage-backed `useWatchlist` store (ids only); this screen
 * resolves those ids against the same active-listings browse the marketplace
 * fetches (GET /marketplace/listings, hand-rolled client) and renders the
 * matching rows. A watched listing that is no longer active simply drops out of
 * the browse result, so it won't appear here — that's the intended behaviour for
 * a degraded, listing-derived watchlist.
 *
 * Design reference: `Documentation/Claude design files/.../buyer/BuyerWatchlistScreen.jsx`
 * — a rich empty state that nudges back to the market, plus a STATIC "±5% price
 * alert" informational notice (copy only; there is no alert backend, so the card
 * describes the intent rather than wiring a real subscription).
 *
 * The route is authed under _app and role-gated to `buyer` via the sidebar nav
 * spec in _app.tsx; the router registration (central) is returned in
 * integrationNeeds. i18n keys live under watchlist.* / marketplace.*.
 */
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Icon,
  IconButton,
  Money,
  Stack,
  StatusPill,
  Text,
} from '@fse/ui';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { FseApiError } from '../api/client';
import { listListings, microToZarp, type ListingDto } from '../api/marketplace';
import { useWatchlist } from '../api/useWatchlist';
import { ListingPhoto } from './ListingPhoto';

export function WatchlistScreen() {
  const { t } = useTranslation();
  const watchlist = useWatchlist();

  // Reuse the SAME query key as the marketplace browse so a buyer arriving from
  // there gets the cached listings instantly (one fetch, one cache).
  const listingsQuery = useQuery({
    queryKey: ['marketplace-listings', 'browse'],
    queryFn: ({ signal }) => listListings({ status: 'active' }, signal),
  });

  const listings = listingsQuery.data?.data ?? [];

  // Resolve watched ids → listing rows, preserving the order they were starred.
  const watched = useMemo(() => {
    const byId = new Map(listings.map((l) => [l.id, l]));
    return watchlist.ids
      .map((id) => byId.get(id))
      .filter((l): l is ListingDto => l != null);
  }, [listings, watchlist.ids]);

  return (
    <Stack spacing={3}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Text variant="headline-medium" as="h2">
            {t('watchlist.title', 'Watchlist')}
          </Text>
          <Box sx={{ mt: 0.5 }}>
            <Text variant="body-medium" color="text.secondary">
              {watchlist.count > 0
                ? t('watchlist.subtitleWithCount', {
                    count: watchlist.count,
                    defaultValue: '{{count}} listings tracked',
                  })
                : t('watchlist.subtitle', 'Listings you are tracking')}
            </Text>
          </Box>
        </Box>
        <Link to="/marketplace" style={{ textDecoration: 'none' }}>
          <Button variant="outlined" startIcon={<Icon name="storefront" size={18} />}>
            {t('watchlist.browseMarket', 'Browse market')}
          </Button>
        </Link>
      </Box>

      {watchlist.count === 0 ? (
        <EmptyState t={t} />
      ) : listingsQuery.isPending ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : listingsQuery.isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => listingsQuery.refetch()}>
              {t('marketplace.retry')}
            </Button>
          }
        >
          {t('marketplace.loadError', {
            error:
              listingsQuery.error instanceof FseApiError
                ? listingsQuery.error.message
                : String(listingsQuery.error),
          })}
        </Alert>
      ) : watched.length === 0 ? (
        // Ids are watched, but none resolve to an active listing right now.
        <Card>
          <CardContent>
            <Stack spacing={1}>
              <Text variant="title-medium" as="h3">
                {t('watchlist.noneActive.title', 'No tracked listings are live')}
              </Text>
              <Text variant="body-medium" color="text.secondary">
                {t(
                  'watchlist.noneActive.body',
                  'Your tracked listings are sold, paused, or withdrawn. Browse the market to add more.',
                )}
              </Text>
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          <Stack spacing={1.5}>
            {watched.map((listing) => (
              <WatchRow
                key={listing.id}
                listing={listing}
                t={t}
                onRemove={() => watchlist.toggle(listing.id)}
              />
            ))}
          </Stack>
          <PriceAlertNotice t={t} />
        </Stack>
      )}
    </Stack>
  );
}

interface WatchRowProps {
  listing: ListingDto;
  t: TFunction;
  onRemove: () => void;
}

/** One watched listing — a horizontal card linking to the detail, with a
 *  filled star to un-watch. */
function WatchRow({ listing, t, onRemove }: WatchRowProps) {
  const askPerKg = microToZarp(listing.askPriceZarpPerKgMicro);
  const seller = listing.sellerCoopName ?? listing.sellerName;

  return (
    <Card sx={{ position: 'relative' }}>
      <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
        {/* Cover thumbnail (TD-135) when present, else the eco-glyph fallback.
            Fixed 88px width, stretches to the row height (no `height` →
            alignSelf:'stretch', matching the original tile). */}
        <ListingPhoto
          listingId={listing.id}
          hasPhoto={listing.hasPhoto}
          width={88}
          glyphSize={40}
          alt={listing.product}
        />

        <Link
          to="/marketplace/$id"
          params={{ id: listing.id }}
          style={{ textDecoration: 'none', flex: 1, minWidth: 0 }}
        >
          <CardContent sx={{ cursor: 'pointer' }}>
            <Stack spacing={0.75}>
              <Text variant="title-medium" as="span" truncate>
                {listing.product}
              </Text>
              <Text variant="label-medium" color="text.secondary">
                {t('marketplace.seller', { name: seller })}
              </Text>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  gap: 1,
                  flexWrap: 'wrap',
                  mt: 0.5,
                }}
              >
                <Money amount={askPerKg} unit={t('marketplace.perKg')} size="md" />
                <StatusPill variant="upcoming" icon="sell">
                  {t('marketplace.offerCount', { count: listing.offerCount })}
                </StatusPill>
              </Box>
            </Stack>
          </CardContent>
        </Link>

        {/* Un-watch (filled star). */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', p: 1 }}>
          <IconButton
            size="small"
            aria-label={t('watchlist.remove', 'Remove from watchlist')}
            onClick={onRemove}
            sx={{ color: 'primary.main' }}
          >
            <Icon name="star" size={22} fill color="inherit" />
          </IconButton>
        </Box>
      </Box>
    </Card>
  );
}

/** Rich empty state — nudges the buyer back to the market. */
function EmptyState({ t }: { t: TFunction }) {
  return (
    <Card>
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: 2,
            py: 4,
          }}
        >
          <Box
            sx={{
              width: 96,
              height: 96,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'action.hover',
              color: 'text.secondary',
            }}
          >
            <Icon name="star" size={48} color="inherit" />
          </Box>
          <Text variant="title-large" as="h3">
            {t('watchlist.empty.title', 'Track listings you care about')}
          </Text>
          <Box sx={{ maxWidth: 360 }}>
            <Text variant="body-medium" color="text.secondary" align="center">
              {t(
                'watchlist.empty.body',
                "Tap the star on any listing to add it here. We'll show its latest price whenever you check back.",
              )}
            </Text>
          </Box>
          <Link to="/marketplace" style={{ textDecoration: 'none' }}>
            <Button variant="contained" startIcon={<Icon name="storefront" size={18} />}>
              {t('watchlist.browseMarket', 'Browse market')}
            </Button>
          </Link>
        </Box>
      </CardContent>
    </Card>
  );
}

/**
 * STATIC informational notice describing the planned ±5% price alert. Copy only
 * — there is no alert subscription backend, so this sets expectation without
 * wiring a real notification.
 */
function PriceAlertNotice({ t }: { t: TFunction }) {
  return (
    <Card sx={{ backgroundColor: 'background.default' }}>
      <CardContent>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
          <Box sx={{ color: 'primary.main', mt: 0.25 }}>
            <Icon name="notifications" size={22} fill color="inherit" />
          </Box>
          <Box>
            <Text variant="title-small" as="h4">
              {t('watchlist.priceAlert.title', 'Price alerts')}
            </Text>
            <Box sx={{ mt: 0.5 }}>
              <Text variant="body-medium" color="text.secondary">
                {t(
                  'watchlist.priceAlert.body',
                  "We'll notify you when a tracked crop's price moves more than ±5% or a new listing appears. Coming soon.",
                )}
              </Text>
            </Box>
          </Box>
        </Box>
        <Divider sx={{ my: 1.5 }} />
        <Text variant="label-small" color="text.disabled">
          {t('watchlist.priceAlert.note', 'Informational only — alerts are not active yet.')}
        </Text>
      </CardContent>
    </Card>
  );
}
